import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { adminSponsorBannersService } from '../../../services/supabaseService';

const SponsorBannersTab = ({ language }) => {
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [formData, setFormData] = useState({
    titleEn: '',
    titleZh: '',
    mediaType: 'image',
    linkUrl: '',
    displayOrder: 0,
    isActive: true,
    file: null
  });

  useEffect(() => {
    loadBanners();
  }, []);

  const loadBanners = async () => {
    try {
      setLoading(true);
      const data = await adminSponsorBannersService?.getAll();
      setBanners(data);
    } catch (error) {
      console.error('Failed to load banners:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e?.target?.files?.[0];
    if (file) {
      setFormData(prev => ({ ...prev, file }));
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!formData?.file) {
      alert(language === 'en' ? 'Please upload a media file' : '请上传媒体文件');
      return;
    }

    try {
      setIsUploading(true);

      // Upload media file
      const { url } = await adminSponsorBannersService?.uploadBannerMedia(formData?.file);

      // Create banner record
      const bannerData = {
        titleEn: formData?.titleEn,
        titleZh: formData?.titleZh,
        mediaUrl: url,
        mediaType: formData?.mediaType,
        linkUrl: formData?.linkUrl || null,
        displayOrder: formData?.displayOrder,
        isActive: formData?.isActive
      };

      await adminSponsorBannersService?.create(bannerData);
      await loadBanners();
      setShowAddModal(false);
      setFormData({
        titleEn: '',
        titleZh: '',
        mediaType: 'image',
        linkUrl: '',
        displayOrder: 0,
        isActive: true,
        file: null
      });

      // Notify home page to refresh banners
      window.dispatchEvent(new CustomEvent('bannerUpdated'));
      // Also use localStorage as fallback
      localStorage.setItem('bannersUpdated', Date.now().toString());
    } catch (error) {
      alert(language === 'en' ? `Failed to create banner: ${error?.message}` : `创建广告失败: ${error?.message}`);
    } finally {
      setIsUploading(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm(language === 'en' ? 'Are you sure you want to delete this banner?' : '确定要删除此广告吗？')) {
      try {
        await adminSponsorBannersService?.delete(id);
        setBanners(banners?.filter(b => b?.id !== id));

        // Notify home page to refresh banners
        window.dispatchEvent(new CustomEvent('bannerUpdated'));
      } catch (error) {
        alert(language === 'en' ? 'Failed to delete banner' : '删除失败');
      }
    }
  };

  const handleToggleActive = async (id, currentStatus) => {
    try {
      await adminSponsorBannersService?.update(id, { isActive: !currentStatus });
      setBanners(banners?.map(b => b?.id === id ? { ...b, isActive: !currentStatus } : b));

      // Notify home page to refresh banners
      window.dispatchEvent(new CustomEvent('bannerUpdated'));
      // Also use localStorage as fallback
      localStorage.setItem('bannersUpdated', Date.now().toString());
    } catch (error) {
      alert(language === 'en' ? 'Failed to update status' : '更新状态失败');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Icon name="Loader2" size={32} className="animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-heading font-semibold text-foreground">
          {language === 'en' ? 'Sponsor Banners Management' : '赞助广告管理'}
        </h2>
        <Button variant="default" iconName="Plus" iconPosition="left" onClick={() => setShowAddModal(true)}>
          {language === 'en' ? 'Add Banner' : '添加广告'}
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {banners?.map((banner) => (
          <div key={banner?.id} className="bg-card border border-border rounded-lg overflow-hidden hover:shadow-md transition-smooth">
            <div className="relative h-48 overflow-hidden bg-muted">
              {banner?.mediaType === 'video' ? (
                <video src={banner?.mediaUrl} className="w-full h-full object-cover" />
              ) : (
                <img src={banner?.mediaUrl} alt={language === 'en' ? banner?.titleEn : banner?.titleZh} className="w-full h-full object-cover" />
              )}
              <div className="absolute top-3 right-3 flex gap-2">
                <span className={`px-2 py-1 rounded-md text-xs font-medium ${banner?.isActive ? 'bg-success text-white' : 'bg-muted text-muted-foreground'}`}>
                  {banner?.isActive ? (language === 'en' ? 'Active' : '活跃') : (language === 'en' ? 'Inactive' : '不活跃')}
                </span>
              </div>
            </div>
            <div className="p-4">
              <h3 className="text-base font-semibold text-foreground mb-2 line-clamp-2">
                {language === 'en' ? banner?.titleEn : banner?.titleZh}
              </h3>
              <div className="space-y-2 mb-3">
                <div className="flex items-center gap-2 text-sm">
                  <Icon name="Image" size={14} color="var(--color-muted-foreground)" />
                  <span className="text-muted-foreground capitalize">{banner?.mediaType}</span>
                </div>
                {banner?.linkUrl && (
                  <div className="flex items-center gap-2 text-sm">
                    <Icon name="Link" size={14} color="var(--color-muted-foreground)" />
                    <span className="text-muted-foreground truncate">{banner?.linkUrl}</span>
                  </div>
                )}
                <div className="flex items-center gap-2 text-sm">
                  <Icon name="Hash" size={14} color="var(--color-muted-foreground)" />
                  <span className="text-muted-foreground">{language === 'en' ? 'Order' : '顺序'}: {banner?.displayOrder}</span>
                </div>
              </div>
              <div className="flex items-center gap-2 pt-3 border-t border-border">
                <button
                  onClick={() => handleToggleActive(banner?.id, banner?.isActive)}
                  className="flex-1 px-3 py-2 rounded-md text-sm font-medium transition-smooth bg-muted hover:bg-muted/80 text-foreground"
                >
                  {banner?.isActive ? (language === 'en' ? 'Deactivate' : '停用') : (language === 'en' ? 'Activate' : '启用')}
                </button>
                <button
                  onClick={() => handleDelete(banner?.id)}
                  className="p-2 rounded-md hover:bg-muted transition-smooth"
                  aria-label="Delete banner"
                >
                  <Icon name="Trash2" size={18} color="var(--color-error)" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {banners?.length === 0 && (
        <div className="text-center py-12">
          <Icon name="Image" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-3" />
          <p className="text-muted-foreground">{language === 'en' ? 'No sponsor banners found' : '未找到赞助广告'}</p>
        </div>
      )}

      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-border flex items-center justify-between">
              <h3 className="text-lg font-semibold text-foreground">
                {language === 'en' ? 'Add Sponsor Banner' : '添加赞助广告'}
              </h3>
              <button onClick={() => setShowAddModal(false)} className="p-1 hover:bg-muted rounded-md">
                <Icon name="X" size={20} />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <Input
                  label={language === 'en' ? 'Title (English)' : '标题（英文）'}
                  value={formData?.titleEn}
                  onChange={(e) => setFormData(prev => ({ ...prev, titleEn: e?.target?.value }))}
                  required
                />
                <Input
                  label={language === 'en' ? 'Title (Chinese)' : '标题（中文）'}
                  value={formData?.titleZh}
                  onChange={(e) => setFormData(prev => ({ ...prev, titleZh: e?.target?.value }))}
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-foreground mb-2 block">
                    {language === 'en' ? 'Media Type' : '媒体类型'} *
                  </label>
                  <select
                    value={formData?.mediaType}
                    onChange={(e) => setFormData(prev => ({ ...prev, mediaType: e?.target?.value }))}
                    className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                    required
                  >
                    <option value="image">{language === 'en' ? 'Image' : '图片'}</option>
                    <option value="video">{language === 'en' ? 'Video' : '视频'}</option>
                  </select>
                </div>
                <Input
                  type="number"
                  label={language === 'en' ? 'Display Order' : '显示顺序'}
                  value={formData?.displayOrder}
                  onChange={(e) => setFormData(prev => ({ ...prev, displayOrder: parseInt(e?.target?.value) }))}
                  required
                />
              </div>

              <Input
                label={language === 'en' ? 'Link URL (Optional)' : '链接地址（可选）'}
                value={formData?.linkUrl}
                onChange={(e) => setFormData(prev => ({ ...prev, linkUrl: e?.target?.value }))}
                placeholder="https://example.com"
              />

              <div>
                <label className="text-sm font-medium text-foreground mb-2 block">
                  {language === 'en' ? 'Upload Media' : '上传媒体'} *
                </label>
                <input
                  type="file"
                  accept={formData?.mediaType === 'video' ? 'video/mp4,video/webm' : 'image/jpeg,image/png,image/webp'}
                  onChange={handleFileChange}
                  className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                  required
                />
                <p className="text-xs text-muted-foreground mt-1">
                  {formData?.mediaType === 'video' 
                    ? (language === 'en' ? 'Supported formats: MP4, WebM (Max 10MB)' : '支持格式：MP4、WebM（最大 10MB）')
                    : (language === 'en' ? 'Supported formats: JPEG, PNG, WebP (Max 10MB)' : '支持格式：JPEG、PNG、WebP（最大 10MB）')
                  }
                </p>
              </div>

              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData?.isActive}
                  onChange={(e) => setFormData(prev => ({ ...prev, isActive: e?.target?.checked }))}
                  className="w-4 h-4 rounded border-border text-primary focus:ring-primary"
                />
                <span className="text-sm text-foreground">{language === 'en' ? 'Active (Show on homepage)' : '活跃（在首页显示）'}</span>
              </label>

              <div className="flex items-center gap-3 pt-4">
                <Button type="submit" disabled={isUploading} className="flex-1">
                  {isUploading ? (language === 'en' ? 'Uploading...' : '上传中...') : (language === 'en' ? 'Create Banner' : '创建广告')}
                </Button>
                <Button type="button" variant="outline" onClick={() => setShowAddModal(false)} className="flex-1">
                  {language === 'en' ? 'Cancel' : '取消'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default SponsorBannersTab;